// Module 10 — the prosumer battery-bar applier: turns SceneSpec.prosumers (§8 encoder output) into a
// vertical CylinderGeometry bar at each prosumer parcel's coord. Geometry is STATIC (built once at a
// fixed display height), so per-tick battery state only mutates the per-instance COLOUR in place — the
// same build-once + recolour discipline as walls/plants (no geometry rebuild = no FPS cliff). State of
// charge rides ALPHA (a fuller battery is brighter), not live bar height: Cesium bakes per-instance
// geometry at build, so animating true height would force a per-tick rebuild — walls treat fillHeightFrac
// the same way. Mode is the hue (charge/discharge/idle each read apart), solar adds a brightness halo, so
// colour is never the only channel (the encoder also carries batteryMotion for a later refinement).
// Boundary: same-module + domain only.
import type { ProsumerSpec } from "./effects/spec";
import type { CesiumLike, SceneLike, PrimitiveLike, GeometryInstanceLike, ColorLike } from "./cesium-api";

const BAR_HEIGHT_M = 520; // fixed display height of every battery bar (soc rides alpha — see header; sized to read at metro zoom)
const BAR_RADIUS_M = 110;

export interface ProsumerBarStyle {
  hex: string;
  alpha: number;
}

// Mode is the identity hue; state-of-charge modulates opacity; solar adds a brightness halo. Each mode
// reads apart by a distinct hue AND value, so colour is never the sole channel (idle is the faintest).
export function prosumerBarStyle(p: ProsumerSpec): ProsumerBarStyle {
  const soc = Math.max(0, Math.min(1, p.socFrac));
  let hex: string;
  let alpha: number;
  switch (p.batteryMode) {
    case "charge": // pulling energy in — Okabe-Ito sky-blue
      hex = "#56B4E9"; alpha = 0.45 + 0.5 * soc; break;
    case "discharge": // pushing energy out to neighbours — Okabe-Ito orange
      hex = "#E69F00"; alpha = 0.45 + 0.5 * soc; break;
    default: // idle — resting battery, neutral grey
      hex = "#999999"; alpha = 0.25 + 0.35 * soc; break;
  }
  if (p.solarGlow) alpha = Math.min(1, alpha + 0.1); // sun on the panels → a brighter bar
  return { hex, alpha };
}

export interface ProsumerLayer {
  apply(prosumers: readonly ProsumerSpec[]): void;
  setShow(visible: boolean): void; // perf-diagnosis lever (primitive.show — no rebuild)
  setModelMatrix(m: unknown): void; // 3D "float" lift — Matrix4 composed over the per-instance placement
}

export function makeProsumerLayer(cesium: CesiumLike, scene: SceneLike): ProsumerLayer {
  let primitive: PrimitiveLike | null = null;
  let liftMatrix: unknown = null; // pending 3D lift if set before the primitive is built
  // identity-stable pickable ids (see plant-columns.ts) — clicking a battery bar selects its load node
  const idOf = new Map<string, { kind: "node"; id: string }>();
  const colorOf = (p: ProsumerSpec): ColorLike => {
    const s = prosumerBarStyle(p);
    return cesium.Color.fromCssColorString(s.hex).withAlpha(s.alpha);
  };

  return {
    apply(prosumers): void {
      if (prosumers.length === 0) return; // v1-shaped snapshot has no prosumer runtimes → nothing to draw
      if (!primitive) {
        const instances: GeometryInstanceLike[] = prosumers.map((p) => {
          const geometry = new cesium.CylinderGeometry({
            length: BAR_HEIGHT_M, topRadius: BAR_RADIUS_M, bottomRadius: BAR_RADIUS_M,
          });
          const origin = cesium.Cartesian3.fromDegrees(p.coord.lon, p.coord.lat, BAR_HEIGHT_M / 2);
          const pickId = { kind: "node" as const, id: p.id };
          idOf.set(p.id, pickId);
          return new cesium.GeometryInstance({
            geometry,
            modelMatrix: cesium.Transforms.eastNorthUpToFixedFrame(origin),
            id: pickId,
            attributes: { color: cesium.ColorGeometryInstanceAttribute.fromColor(colorOf(p)) },
          });
        });
        primitive = new cesium.Primitive({
          geometryInstances: instances,
          appearance: new cesium.PerInstanceColorAppearance({ flat: false, translucent: true }),
          asynchronous: true, // build off-thread (default); never block the main thread on geometry compile (see walls.ts)
        });
        if (liftMatrix != null) primitive.modelMatrix = liftMatrix; // apply a lift set before build
        scene.primitives.add(primitive);
        return;
      }
      // already built — recolour in place. Skip until the primitive has rendered once: Cesium refuses
      // getGeometryInstanceAttributes before its first update pass (a hidden tab pauses rAF while the
      // worker keeps posting snapshots); the build-time colour holds until the next rendered frame.
      if (!primitive.ready) return;
      for (const p of prosumers) {
        const pickId = idOf.get(p.id);
        if (!pickId) continue;
        const attr = primitive.getGeometryInstanceAttributes(pickId);
        attr.color = cesium.ColorGeometryInstanceAttribute.toValue(colorOf(p), attr.color); // real Cesium API: toValue(color, result)
      }
    },
    setShow(visible): void {
      if (primitive) primitive.show = visible;
    },
    setModelMatrix(m): void {
      liftMatrix = m;
      if (primitive) primitive.modelMatrix = m;
    },
  };
}
